/**
 * RBAC Error Messages
 * Centralized constants for permission-related error messages
 *
 * Usage:
 * ```typescript
 * import { getRbacErrorMessage } from '../../constants/rbacMessages';
 *
 * const mutation = useSomeMutation({
 *   onError: (error) => {
 *     alert(getRbacErrorMessage(error));
 *   }
 * });
 * ```
 *
 * The getRbacErrorMessage() function will:
 * - Return a user-friendly message for 403 (permission denied) errors
 * - Return a user-friendly message for 401 (unauthorized) errors
 * - Return the backend's custom message if available
 * - Fall back to a generic error message for other errors
 */

export const RBAC_MESSAGES = {
  PERMISSION_DENIED:
    "You do not have permission to perform this operation. Please contact your administrator if you believe you should have access.",
  INSUFFICIENT_PERMISSIONS:
    "Your current role does not allow this action. Contact your administrator for access.",
  UNAUTHORIZED: "Please log in to access this resource.",
  SESSION_EXPIRED: "Your session has expired. Please log in again.",
} as const;

/**
 * Check if an error is a permission denied error (403)
 */
export function isPermissionDeniedError(error: unknown): boolean {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { status?: number } }).response;
    return response?.status === 403;
  }
  return false;
}

/**
 * Check if an error is an unauthorized error (401)
 */
export function isUnauthorizedError(error: unknown): boolean {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { status?: number } }).response;
    return response?.status === 401;
  }
  return false;
}

/**
 * Get appropriate RBAC error message based on error type
 */
export function getRbacErrorMessage(error: unknown): string {
  // Handle standard Error objects (from fetch API)
  if (error instanceof Error) {
    const message = error.message;

    // Check if the error message contains the backend's permission denied message
    if (
      message.includes("do not have permission") ||
      message.includes("not authorised") ||
      message.includes("Unauthorised")
    ) {
      return message;
    }

    // Check for unauthorized
    if (
      message.includes("Unauthorized") ||
      message.includes("userSub is missing")
    ) {
      return RBAC_MESSAGES.UNAUTHORIZED;
    }
  }

  // Handle axios-style errors (if any part of the app uses axios)
  if (isUnauthorizedError(error)) {
    return RBAC_MESSAGES.UNAUTHORIZED;
  }
  if (isPermissionDeniedError(error)) {
    // Check if backend returned a custom message
    if (
      error &&
      typeof error === "object" &&
      "response" in error &&
      error.response &&
      typeof error.response === "object" &&
      "data" in error.response &&
      error.response.data &&
      typeof error.response.data === "object" &&
      "message" in error.response.data
    ) {
      return String(error.response.data.message);
    }
    return RBAC_MESSAGES.PERMISSION_DENIED;
  }

  return "An error occurred. Please try again.";
}
